import React, { useEffect } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import RecordPickerController from './controllers/RecordPickerController';
import AhaCreateRecordMenuButton from 'components/common/AhaCreateRecordMenuButton';
import {
  AhaButton,
  AhaModal,
  AhaModalBody,
  AhaModalFooter,
  AhaModalHeader,
} from 'components/common/reactifyWebComponent';
import pluralize from 'javascripts/util/pluralize';
import RecordPickerContent from './components/RecordPickerContent';
import { type SearchableDocumentUnion } from 'components/framework/models/generated/types';

const RecordPickerModal: React.FC = () => {
  const controller = useController(RecordPickerController);
  const {
    actionLabel,
    open,
    selection,
    title,
    multipleRecords,
    createRecord,
    defaultName,
    onSelect,
    parentRecordType,
    parentRecordId,
  } = controller.state;

  useEffect(() => {
    // listen for a specific record creation annotation event
    // this is done by an after_view create response when adding a record from the record picker
    const handleCreationEvent = (
      event: CustomEvent<{
        record: {
          typename: string;
          id: string;
          reference_num: string;
          path: string;
        };
      }>
    ) => {
      const {
        detail: { record },
      } = event;
      if (onSelect) {
        onSelect([
          {
            searchable: {
              typename: record.typename,
              id: record.id,
              referenceNum: record.reference_num,
              path: record.path,
            } as SearchableDocumentUnion,
          },
        ]);
      }
    };
    document.addEventListener(
      'aha.record_picker:record_created',
      handleCreationEvent
    );

    return () => {
      document.removeEventListener(
        'aha.record_picker:record_created',
        handleCreationEvent
      );
    };
  }, [onSelect]);

  return (
    <div>
      <AhaModal open={open} onClose={() => (controller.state.open = false)}>
        <AhaModalHeader modalTitle={title || 'Select Aha! records'} />
        <AhaModalBody
          style={{
            minWidth: 850,
            height: 700,
            minHeight: 300,
          }}
        >
          <RecordPickerContent />
        </AhaModalBody>
        <AhaModalFooter>
          <AhaButton
            onClick={() => controller.actionClose()}
            slot='secondary_actions'
          >
            Cancel
          </AhaButton>
          {createRecord && (
            <AhaCreateRecordMenuButton
              scope='RecordPickerController'
              afterView='link_record_tool'
              defaultName={defaultName}
              parentRecordType={parentRecordType}
              parentRecordId={parentRecordId}
              onSelect={() => controller.actionClose()}
            />
          )}
          {multipleRecords && (
            <span className='mr-5'>
              {selection.length > 0 && (
                <>
                  <strong>{selection.length}</strong>{' '}
                  {pluralize(selection.length, 'record')} selected
                </>
              )}
            </span>
          )}
          <AhaButton
            disabled={selection.length === 0}
            kind='primary'
            title={selection.length === 0 ? 'Select records' : null}
            onClick={() => controller.actionSelectRecords()}
          >
            {actionLabel || 'Select records'}
          </AhaButton>
        </AhaModalFooter>
      </AhaModal>
    </div>
  );
};

export default ApplicationView(RecordPickerModal);
